import { AppCard, AppPage } from '../../components/general';
import { BreadcrumbItem } from '../../components/general/AppPage';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { useAuth } from '@mezzodevelopment/mezzo-ui/auth';
import { useState, useRef } from 'react';
import { Waterfalls } from './Waterfalls';
import { WaterfallMigrationReview } from './WaterfallMigrationReview';
import { useWaterfalls } from '../../hooks/useWaterfalls';
import { WaterfallViewModel, WaterfallMigrationDiff, WaterfallMigrationImportRequest } from '../../models/waterfall';
import { exportMigration, prepareImportMigration, importMigration } from '../../services/waterfallService';

interface WaterfallsContainerProps {
  integrationType?: string;
  title?: string;
  basePath?: string;
  breadcrumbs?: BreadcrumbItem[];
}

export const WaterfallsContainer = ({ integrationType, title = 'Waterfalls', basePath = '/waterfalls', breadcrumbs }: WaterfallsContainerProps) => {
  const { user, isLoading: authIsLoading } = useAuth();
  const { waterfallsQuery, deleteWaterfall } = useWaterfalls();
  const [migrationDiff, setMigrationDiff] = useState<WaterfallMigrationDiff | null>(null);
  const toastRef = useRef<Toast>(null);

  const isLoading = authIsLoading || !user || waterfallsQuery.isLoading;

  const handleMigrationExport = async () => {
    try {
      const blob = await exportMigration();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `waterfall-migration-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toastRef.current?.show({
        severity: 'success',
        summary: 'Export Completed',
        detail: 'Migration file downloaded successfully',
        life: 3000,
      });
    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Export Failed',
        detail: error instanceof Error ? error.message : 'Failed to export migration',
        life: 5000,
      });
      throw error;
    }
  };

  const handleMigrationPrepareImport = async (file: File) => {
    const diff = await prepareImportMigration(file);
    setMigrationDiff(diff);
  };

  const handleMigrationImport = async (request: WaterfallMigrationImportRequest) => {
    const result = await importMigration(request);

    if (result.success) {
      waterfallsQuery.refetch();
    }

    return result;
  };

  const handleMigrationCancel = () => {
    setMigrationDiff(null);
  };

  if (isLoading) {
    return (
      <AppPage title={title} breadcrumbs={breadcrumbs || [{ label: 'Dashboard', to: '/dashboard' }, { label: title }]}>
        <AppCard>
          <div className="flex flex-column align-items-center justify-content-center gap-2">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
            <span>Loading Waterfalls...</span>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  if (migrationDiff) {
    return (
      <>
        <Toast ref={toastRef} />
        <WaterfallMigrationReview
          diff={migrationDiff}
          onImport={handleMigrationImport}
          onCancel={handleMigrationCancel}
          breadcrumbs={breadcrumbs}
        />
      </>
    );
  }

  return (
    <>
      <Toast ref={toastRef} />
      <Waterfalls
        waterfalls={(waterfallsQuery.data ?? []) as WaterfallViewModel[]}
        onDelete={(id) => {
          if (id == null) return;
          deleteWaterfall(id);
        }}
        integrationType={integrationType}
        title={title}
        basePath={basePath}
        breadcrumbs={breadcrumbs}
        onMigrationExport={handleMigrationExport}
        onMigrationPrepareImport={handleMigrationPrepareImport}
      />
    </>
  );
};
